import { defineStore } from 'pinia'
import { ref, computed, watch, nextTick, reactive } from 'vue'
import { useApi } from '@/utils/axios'
import { PEDIDO_ROTAS, CLIENTE_ROTAS, REVENDEDOR_ROTAS } from '@/utils/apiRoutes'

// Constants
const STORAGE_KEY = 'pedidosColumnVisibility'
const ORDER_STORAGE_KEY = 'pedidosColumnOrder'
const DEFAULT_PAGINATION = { page: 1, pageSize: 20 }

// Buscar data atual com formatacao YYYY-MM-DD
const dataAtualString = () => {
  const today = new Date()

  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

// Buscar data atual com formatacao YYYY-MM-DD + dias
const dataAtualMaisDiasString = (days) => {
  const date = new Date()
  date.setDate(date.getDate() + days)

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

// Helper to create API parameters consistent way
const criarParametrosParaApi = (filters, columnType, page = 1, pageSize = 20) => {
  const params = {
    page,
    per_page: pageSize,
    filtro: columnType,
    data_inicial: filters.dataInicio,
    data_final: filters.dataFim,
    nome_fantasia: filters.nomeFantasia,
    razao_social: filters.razaoSocial,
    telefone: filters.telefoneCliente,
    merece_atencao: filters.merecemAtencao ? 1 : null,
    area_externa: filters.areaExterna ? 1 : null,
    agendados: filters.agendados ? 1 : null,
    assistencia_tecnica: filters.assistencia ? 1 : null,
    pendentes: filters.pendentes ? 1 : null,
    sem_gestor: filters.semGestor ? 1 : null,
    gas_do_povo: filters.gasDoPovo ? 1 : null,
    gestor_id: filters.gestor || null,
    categoria_produto: filters.categoriaProduto || null,
  }

  if (filters.origem.length > 0) {
    params.origem = filters.origem.join(',')
  }

  if (filters.estados.length > 0) {
    params.estados = filters.estados.join(',')
  }

  return params
}

export const usePedidoStore = defineStore('pedido', () => {
  // State with better organization
  const state = reactive({
    data: null,
    loading: false,
    error: null,
    activeTab: {},
    lastUpdated: null,
    pagination: { ...DEFAULT_PAGINATION },
    columnData: {}, // Store data for each column separately
    columnMeta: {}, // Store meta information for each column (pagination data)
    columnLoading: {}, // Track loading state for each column
  })

  // Using ref instead of reactive for arrays to maintain reactivity when items are replaced
  const statusColumns = ref([])

  const managersColumns = ref([])

  // Performance helpers for manager search
  const managerSearchTimeout = ref(null)
  let managerAbortController = null
  const managerCache = new Map()

  function getListManagers(nameSearch = '') {
    const term = (nameSearch || '').trim().toLowerCase()

    // Always filter locally first from existing managersColumns for instant results
    const localMatches = managersColumns.value.filter((manager) =>
      manager.title.toLowerCase().includes(term),
    )
    managersColumns.value = localMatches

    // Call API for initial load (empty term) or searches with >= 3 chars
    if (term.length === 0 || term.length >= 3) {
      // Serve from cache if available (simple exact-match cache)
      if (managerCache.has(term)) {
        managersColumns.value = managerCache.get(term)
        return
      }

      // Debounce subsequent API calls (350ms), but not for initial load
      if (term.length > 0) {
        if (managerSearchTimeout.value) {
          clearTimeout(managerSearchTimeout.value)
        }

        managerSearchTimeout.value = setTimeout(async () => {
          await performApiCall(term)
        }, 350)
      } else {
        // Immediate call for initial load
        performApiCall(term)
      }
    }

    async function performApiCall(searchTerm) {
      // Abort previous request if still in-flight
      if (managerAbortController) {
        try {
          managerAbortController.abort()
        } catch (e) { }
        managerAbortController = null
      }

      managerAbortController = new AbortController()
      try {
        /*
        id: number
        idrevendedor: number
        nome: string
        email: string
        */
        const response = await useApi(REVENDEDOR_ROTAS.LISTAR_REVENDEDOR_GESTOR_CONTA).fetch({
          params: {
            search: searchTerm,
            per_page: 100,
          },
          signal: managerAbortController.signal,
        })

        const data = (response.data || []).map((manager) => ({
          title: manager?.nome || '',
          value: manager?.id || null,
        }))

        // update store and cache
        managersColumns.value = data
        try {
          managerCache.set(searchTerm, data)
          // keep cache small: optional simple eviction when grows too large
          if (managerCache.size > 50) {
            const firstKey = managerCache.keys().next().value
            managerCache.delete(firstKey)
          }
        } catch (e) {
          // ignore cache errors
        }
      } catch (err) {
        if (err.name === 'AbortError') {
          // request was aborted intentionally — ignore
        } else {
          // optionally handle other errors (silent to avoid polluting UI)
          console.error('Erro ao buscar gestores:', err)
        }
      } finally {
        managerSearchTimeout.value = null
        managerAbortController = null
      }
    }
  }

  // Filter state with smarter defaults
  const filtros = ref({
    dataInicio: dataAtualString(),
    dataFim: dataAtualMaisDiasString(3),
    origem: [],
    merecemAtencao: false,
    areaExterna: false,
    agendados: false,
    assistencia: false,
    pendentes: false,
    semGestor: false,
    gasDoPovo: false,
    nomeFantasia: '',
    razaoSocial: '',
    numeroPedido: '',
    estados: [],
    telefoneCliente: '',
    gestor: null,
    categoriaProduto: null, // agua | gas
  })

  // State tracking
  const columnMenuOpen = ref(false)
  const columnApiCalls = reactive({})

  // Computed properties with better memoization
  const pedidos = computed(() => state.data || [])

  // Define column types and their API filter values
  const columnTypes = {
    aceitar: 'aguardandoaceite',
    atrasados: 'atrasados',
    suspeitos: 'suspeitos',
    emandamento: 'emandamento',
    entregues: 'entregues',
    cancelados: 'cancelados',
    mereceatencao: 'mereceatencao',
  }

  // Map de mapeamento inverso: escopo_atual para chave de coluna
  const escopoToColumnKey = {
    aguardandoaceite: 'aceitar',
    atrasados: 'atrasados',
    suspeitos: 'suspeitos',
    emandamento: 'emandamento',
    entregues: 'entregues',
    cancelados: 'cancelados',
    // Nota: Não adicionamos mereceatencao aqui, pois não é um status real, mas uma visão abstrata
  }

  // Function to get pedidos by status key with error checking
  const getPedidosByStatus = (status) => {
    // Handle the special case for 'atencao' column
    if (status === 'atencao') {
      return getPedidosEmAtencao()
    }

    // For all other columns, return the data from the state
    return state.columnData[status] || []
  }

  // Column visibility computed properties
  const visibleColumns = computed(() => statusColumns.value.filter((column) => column.visible))

  const hasVisibleColumns = computed(() => statusColumns.value.some((column) => column.visible))

  // Initialize store with better error handling
  function initialize() {
    try {
      setupStatusColumns()
      loadColumnVisibility()
      carregarOrdemDasColunas()
      // Initialize pedidos em atenção
      initializePedidosEmAtencao()

      getListManagers()
      return Promise.resolve()
    } catch (error) {
      return Promise.reject(error)
    }
  }

  // Setup status columns with default visibility
  function setupStatusColumns() {
    statusColumns.value = [
      { title: 'A aceitar', color: '#7FFFD4', key: 'aceitar', visible: true },
      { title: 'Atrasados', color: '#FFFF00', key: 'atrasados', visible: true },
      { title: 'Suspeitos', color: '#EAC413', key: 'suspeitos', visible: true },
      { title: 'Em andamento', color: '#dff0d8', key: 'emandamento', visible: true },
      { title: 'Cancelados', color: '#ff2222', key: 'cancelados', visible: true },
      { title: 'Entregues', color: '#d9edf7', key: 'entregues', visible: true },
      { title: 'Merece Atenção', color: '#ff8f00', key: 'mereceatencao', visible: true },
      { title: 'Atenção', color: '#9C27B0', key: 'atencao', visible: true }, // New column
    ]

    // Initialize column data and meta
    statusColumns.value.forEach((column) => {
      if (!state.columnData[column.key]) {
        state.columnData[column.key] = []
      }
      if (!state.columnMeta[column.key]) {
        state.columnMeta[column.key] = {
          currentPage: 1,
          total: 0,
          perPage: 20,
          lastPage: 1,
        }
      }
      state.columnLoading[column.key] = false
    })
  }

  // Adicionar estado para snackbar de erros
  const errorSnackbar = reactive({
    show: false,
    message: '',
    color: 'error',
    timeout: 5000,
  })

  // Fetch data for all visible columns
  const fetchPedidos = async (options = {}) => {
    if (!options.silent) {
      state.loading = true
      state.error = null
    }

    try {
      // Reset column data if not loading more
      if (!options.loadMore) {
        visibleColumns.value.forEach((column) => {
          state.columnData[column.key] = []
          state.columnMeta[column.key] = {
            currentPage: 1,
            total: 0,
            perPage: 20,
            lastPage: 1,
          }
        })
      }

      // Verificar se está pesquisando por ID específico
      if (filtros.value.numeroPedido && filtros.value.numeroPedido.trim() !== '') {
        try {
          // Se pesquisando por ID, primeiro busca o pedido em qualquer coluna para descobrir o escopo_atual
          const pedidoResponse = await useApi(
            PEDIDO_ROTAS.PEDIDO(filtros.value.numeroPedido),
          ).fetch()

          if (pedidoResponse?.data && pedidoResponse.data.escopo_atual) {
            // Mapeie o escopo_atual para a coluna apropriada
            const columnKey = escopoToColumnKey[pedidoResponse.data.escopo_atual] || 'emandamento'

            // Verifica se a coluna de destino está visível
            const targetColumn = statusColumns.value.find((col) => col.key === columnKey)

            if (targetColumn && targetColumn.visible) {
              // Coloca o pedido na coluna apropriada
              state.columnData[columnKey] = [pedidoResponse.data]
              state.columnMeta[columnKey] = {
                currentPage: 1,
                total: 1,
                perPage: 20,
                lastPage: 1,
              }

              // Limpa outras colunas para mostrar apenas o pedido pesquisado
              visibleColumns.value.forEach((column) => {
                if (column.key !== columnKey) {
                  state.columnData[column.key] = []
                  state.columnMeta[column.key] = {
                    currentPage: 1,
                    total: 0,
                    perPage: 20,
                    lastPage: 1,
                  }
                }
              })

              // Configura a aba ativa para o pedido
              if (pedidoResponse.data.id && !state.activeTab[pedidoResponse.data.id]) {
                state.activeTab[pedidoResponse.data.id] = 'tabPedido'
              }

              state.lastUpdated = new Date()
              return { success: true }
            }
          }

          // Caso o pedido seja encontrado mas a coluna não esteja visível
          if (pedidoResponse?.data) {
            // Mostrar notificação de que o pedido foi encontrado mas está em uma coluna oculta
            Object.assign(errorSnackbar, {
              show: true,
              message:
                'Pedido encontrado, mas está em uma coluna que está oculta. Exiba todas as colunas para visualizá-lo.',
              color: 'warning',
              timeout: 6000,
            })

            // Não carregar dados das outras colunas neste caso
            state.lastUpdated = new Date()
            return { success: true, warning: 'column-hidden' }
          }
        } catch (err) {
          if (err.response && err.status === 422) {
            // Extrair e mostrar mensagem de erro
            const errorMessage = err.response.data?.message || 'Pedido não encontrado'

            // Forçar atualização do estado reativo usando Object.assign
            Object.assign(errorSnackbar, {
              show: true,
              message: errorMessage,
              color: 'error',
              timeout: 5000,
            })

            // Limpar colunas para mostrar estado vazio
            visibleColumns.value.forEach((column) => {
              state.columnData[column.key] = []
              state.columnMeta[column.key] = {
                currentPage: 1,
                total: 0,
                perPage: 20,
                lastPage: 1,
              }
            })

            // Importante: retornar aqui para não continuar com a busca em outras colunas
            state.lastUpdated = new Date()
            return { success: false, error: errorMessage }
          }
        }

        // Se chegou aqui, significa que o pedido não foi encontrado por ID diretamente
        // Neste caso, não devemos continuar com as buscas por colunas
        Object.assign(errorSnackbar, {
          show: true,
          message: `Pedido #${filtros.value.numeroPedido} não encontrado`,
          color: 'warning',
          timeout: 5000,
        })

        state.lastUpdated = new Date()
        state.loading = false
        return { success: false, error: 'Pedido não encontrado' }
      }

      // Se não estiver pesquisando por ID ou se o pedido não foi encontrado,
      // procede com a pesquisa normal em todas as colunas
      const promises = visibleColumns.value.map((column) =>
        buscarPedidosPorColuna(column.key, options),
      )

      await Promise.all(promises)
      state.lastUpdated = new Date()

      return { success: true }
    } catch (err) {
      if (!options.silent && err.name !== 'AbortError') {
        state.error = err.message || 'Erro ao carregar pedidos'
        // Mostrar erro no snackbar se for um erro do servidor
        if (err.response) {
          errorSnackbar.message = err.response.data?.message || state.error
          errorSnackbar.show = true
        }
      }
    } finally {
      if (!options.silent) {
        state.loading = false
      }
    }
  }

  // Buscar dados para uma coluna específica
  const buscarPedidosPorColuna = async (columnKey, options = {}) => {
    //se existir um pedido no filtro, pesquisa apenas por ele
    if (filtros.value.numeroPedido && filtros.value.numeroPedido.trim() !== '') {
      //descobrir coluna onde se encontra o pedido para atualizar ou não seus dados
      const sourceColumnKey = findPedidoColumn(parseInt(filtros.value.numeroPedido.trim()))
      if (sourceColumnKey && sourceColumnKey == columnKey) {
        fetchPedidos({ silent: true })
      }
      return []
    }

    if (
      options.loadMore &&
      state.columnMeta[columnKey] &&
      state.columnMeta[columnKey].currentPage >= state.columnMeta[columnKey].lastPage
    ) {
      return []
    }

    // Cancel previous in-flight request if it exists and not loading more
    if (columnApiCalls[columnKey] && !options.silent && !options.loadMore) {
      columnApiCalls[columnKey].abort()
    }

    // Set column loading state
    state.columnLoading[columnKey] = true

    try {
      // Special handling for 'atencao' column which doesn't have a backend filter
      if (columnKey === 'atencao') {
        // Use atualizarPedidosEmAtencao to get data for the attention column
        await atualizarPedidosEmAtencao()

        // Get the data from pedidosEmAtencaoMap
        const pedidosData = getPedidosEmAtencao()

        // Update column data
        if (options.append) {
          // In case of append, we would just keep the existing data
          // since atualizarPedidosEmAtencao already updates the entire list
        } else {
          // Update the column data with the attention pedidos
          state.columnData[columnKey] = pedidosData
        }

        // Update metadata for the attention column
        state.columnMeta[columnKey] = {
          currentPage: 1,
          total: pedidosData.length,
          perPage: state.pagination.pageSize,
          lastPage: 1, // Only one page for attention column since it's all in memory
        }

        return pedidosData
      }

      // Regular column handling for other columns
      const currentPage = state.columnMeta[columnKey]?.currentPage || 1
      const isAutoUpdate = options.autoUpdate || false

      // Ajustar per_page para buscar todas as páginas de uma vez em atualizações automáticas
      const perPage = isAutoUpdate
        ? currentPage * state.pagination.pageSize
        : state.pagination.pageSize

      const page = options.append ? currentPage + 1 : 1

      const params = criarParametrosParaApi(filtros.value, columnTypes[columnKey], page, perPage)

      const response = await useApi(PEDIDO_ROTAS.LISTAR_PEDIDOS).fetch({
        params,
        signal: options.signal,
      })

      if (response?.data) {
        if (isAutoUpdate) {
          // Ver se algum pedido estava em outra coluna e remover
          response.data.forEach((pedido) => {
            const sourceColumnKey = findPedidoColumn(pedido.id)

            if (sourceColumnKey && sourceColumnKey !== columnKey) {
              removerPedidoDaColuna(sourceColumnKey, pedido.id, true)
            }
          })

          // Substituir os pedidos existentes pelos atualizados
          state.columnData[columnKey] = response.data
        } else if (options.append) {
          // Adicionar novos pedidos ao final da lista existente
          state.columnData[columnKey] = [...state.columnData[columnKey], ...response.data]
        } else {
          // Substituir os pedidos existentes
          state.columnData[columnKey] = response.data
        }
      }

      // Atualizar metadados da coluna
      if (response?.meta) {
        const total = response.meta.total
        state.columnMeta[columnKey] = {
          currentPage: isAutoUpdate
            ? currentPage // Manter o currentPage para scroll
            : response.meta.current_page,
          total: total,
          perPage: state.pagination.pageSize, // Garantir que perPage seja consistente
          lastPage: Math.ceil(total / state.pagination.pageSize), // Calcular corretamente o lastPage
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        throw err
      }
      return []
    } finally {
      columnApiCalls[columnKey] = null
      state.columnLoading[columnKey] = false
    }
  }

  // Method to load more data for a specific column (for infinite scroll)
  const buscarMaisPedidosColuna = async (columnKey) => {
    if (!columnKey || state.columnLoading[columnKey]) {
      return
    }

    if (
      !state.columnMeta[columnKey] ||
      state.columnMeta[columnKey].currentPage >= state.columnMeta[columnKey].lastPage
    ) {
      return
    }

    await buscarPedidosPorColuna(columnKey, { loadMore: true, silent: true })
  }

  // Check if a column has more data to load
  const existemPedidosParaCarregar = (columnKey) => {
    if (!state.columnMeta[columnKey]) return false

    return state.columnMeta[columnKey].currentPage < state.columnMeta[columnKey].lastPage
  }

  // Check if a column is currently loading
  const isColumnLoading = (columnKey) => {
    return state.columnLoading[columnKey] || false
  }

  // Get total count for a column
  const getColumnTotal = (columnKey) => {
    return state.columnMeta[columnKey]?.total || 0
  }

  // Apply filters with optional pagination reset
  const aplicarFiltros = () => {
    fetchPedidos({ loadMore: false })
  }

  // Reset filters - we'll enhance this to be more comprehensive
  const limparFiltros = () => {
    // Reset all filter values to defaults
    filtros.value = {
      dataInicio: dataAtualString(),
      dataFim: dataAtualMaisDiasString(3),
      origem: [],
      merecemAtencao: false,
      areaExterna: false,
      agendados: false,
      assistencia: false,
      pendentes: false,
      semGestor: false,
      gasDoPovo: false,
      nomeFantasia: '',
      razaoSocial: '',
      numeroPedido: '',
      estados: [],
      telefoneCliente: '',
      categoriaProduto: null,
      gestor: null,
    }

    // Also reset any search text if that's tracked separately
    if (searchTimeout.value) {
      clearTimeout(searchTimeout.value)
      searchTimeout.value = null
    }

    // Clear any specific pedido ID being viewed
    if (filtros.value.numeroPedido !== '') {
      // Clear existing columns that might have been filtered
      visibleColumns.value.forEach((column) => {
        state.columnData[column.key] = []
      })
    }

    // Fetch fresh data with reset filters
    return fetchPedidos({ loadMore: false })
  }

  // Column visibility actions
  const toggleAllColumns = (value) => {
    statusColumns.value.forEach((column) => {
      column.visible = value
    })
    saveColumnVisibility()
    // Refetch data for newly visible columns
    fetchPedidos({ loadMore: false })
  }

  // Improved localStorage handling with error recovery
  const saveColumnVisibility = () => {
    try {
      const visibilityState = {}
      statusColumns.value.forEach((column) => {
        visibilityState[column.key] = column.visible
      })

      localStorage.setItem(STORAGE_KEY, JSON.stringify(visibilityState))
    } catch (e) { }
  }

  const loadColumnVisibility = () => {
    try {
      const savedState = localStorage.getItem(STORAGE_KEY)
      if (!savedState) return

      const visibilityState = JSON.parse(savedState)

      if (statusColumns.value.length > 0) {
        statusColumns.value.forEach((column) => {
          if (visibilityState.hasOwnProperty(column.key)) {
            column.visible = Boolean(visibilityState[column.key])
          }
        })
      }
    } catch (e) {
      // Recover by ensuring at least one column is visible
      if (statusColumns.value.length > 0) {
        statusColumns.value[0].visible = true
      }
    }
  }

  // Improved column order handling
  const salvarOrdemDasColunas = () => {
    try {
      const orderState = statusColumns.value.map((column) => column.key)
      localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(orderState))
    } catch (e) { }
  }

  const carregarOrdemDasColunas = () => {
    try {
      const savedOrder = localStorage.getItem(ORDER_STORAGE_KEY)
      if (!savedOrder) return

      const orderState = JSON.parse(savedOrder)

      if (Array.isArray(orderState) && orderState.length > 0 && statusColumns.value.length > 0) {
        // Create a map of current columns for faster lookup
        const columnsMap = new Map(statusColumns.value.map((col) => [col.key, col]))

        // Create a new array based on the saved order
        const reorderedColumns = []

        // Add columns in the saved order if they exist
        for (const key of orderState) {
          const column = columnsMap.get(key)
          if (column) {
            reorderedColumns.push(column)
            columnsMap.delete(key) // Remove from map to track remaining columns
          }
        }

        // Add any remaining columns (new ones since last save)
        for (const column of columnsMap.values()) {
          reorderedColumns.push(column)
        }

        // Update if we have all columns
        if (reorderedColumns.length > 0) {
          statusColumns.value = reorderedColumns
        }
      }
    } catch (e) { }
  }

  // More efficient helper methods
  const getPedidosLength = (statusKey) => {
    return state.columnMeta[statusKey]?.total || getPedidosByStatus(statusKey).length || 0
  }

  const hasPedidos = (statusKey) => {
    return getPedidosLength(statusKey) > 0
  }

  // Improved tab management
  const setActiveTab = (pedidoId, tabName) => {
    state.activeTab[pedidoId] = tabName
  }

  // Advanced search with debounce
  const searchTimeout = ref(null)

  // Watch for filter changes and auto-apply
  watch(
    () => filtros.value.merecemAtencao,
    () => aplicarFiltros(),
  )

  watch(
    () => filtros.value.areaExterna,
    () => aplicarFiltros(),
  )

  watch(
    () => filtros.value.agendados,
    () => aplicarFiltros(),
  )

  watch(
    () => filtros.value.assistencia,
    () => aplicarFiltros(),
  )

  watch(
    () => filtros.value.pendentes,
    () => aplicarFiltros(),
  )

  watch(
    () => filtros.value.semGestor,
    (newVal) => {
      if (newVal) {
        // when filtering by "semGestor" ensure any selected gestor is cleared
        filtros.value.gestor = null
      }
      aplicarFiltros()
    },
  )

  watch(
    () => filtros.value.gasDoPovo,
    () => aplicarFiltros(),
  )

  watch(
    () => filtros.value.categoriaProduto,
    () => aplicarFiltros(),
  )

  // Add method to edit pedido - optimized version
  const editarPedido = async (pedidoId, data) => {
    if (!pedidoId) return { success: false, message: 'ID do pedido não informado' }

    try {
      const response = await useApi(PEDIDO_ROTAS.PEDIDO(pedidoId)).put(data)

      // Check response data format
      if (response.status === 'success') {
        // Update local state if data is returned
        if (response.data) {
          // Update the pedido in all column data where it exists
          for (const key in state.columnData) {
            const index = state.columnData[key].findIndex((p) => p.id === pedidoId)
            if (index !== -1) {
              state.columnData[key][index] = { ...state.columnData[key][index], ...response.data }
            }
          }
        }

        return {
          success: true,
          data: response.data,
        }
      }

      return {
        success: false,
        error: response.data,
      }
    } catch (error) {
      return {
        success: false,
        error,
      }
    }
  }

  const avisarPedido = async (pedidoId, revendedorId) => {
    if (!pedidoId || !revendedorId) {
      return {
        success: false,
        message: 'ID do pedido ou do revendedor não informado',
      }
    }

    try {
      const response = await useApi(PEDIDO_ROTAS.AVISAR_PEDIDO(pedidoId, revendedorId)).fetch()

      return {
        success: response.status === 'success',
        message: 'Notificação enviada com sucesso!',
      }
    } catch (error) {
      return {
        success: false,
        message: 'Erro ao enviar notificação.',
      }
    }
  }

  const atualizarObsBoasVindas = async (revendedorId, obsText) => {
    if (!revendedorId) {
      return {
        success: false,
        message: 'ID do revendedor não informado',
      }
    }

    try {
      const response = await useApi(REVENDEDOR_ROTAS.OBS_BOAS_VINDAS(revendedorId)).put({
        obsboasvindas: obsText,
      })

      return {
        success: true,
        message: 'Observação atualizada com sucesso!',
        data: response.data,
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.message || 'Erro ao atualizar observação',
        error,
      }
    }
  }

  // Add method to update pedido's observacao
  const atualizarObservacaoPedido = async (pedidoId, observacaoText) => {
    if (!pedidoId) {
      return {
        success: false,
        message: 'ID do pedido não informado',
      }
    }

    try {
      const response = await useApi(PEDIDO_ROTAS.OBSERVACAO_PEDIDO(pedidoId)).put({
        observacao: observacaoText,
      })

      return {
        success: true,
        message: 'Observação do pedido atualizada com sucesso!',
        data: response.data,
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.message || 'Erro ao atualizar observação do pedido',
        error,
      }
    }
  }

  // Add method to update pedido's observacao_aplicativo
  const atualizarObservacaoAplicativo = async (pedidoId, observacaoText) => {
    if (!pedidoId) {
      return {
        success: false,
        message: 'ID do pedido não informado',
      }
    }

    try {
      const response = await useApi(PEDIDO_ROTAS.OBSERVACAO_APLICATIVO(pedidoId)).put({
        observacao_aplicativo: observacaoText,
      })

      return {
        success: true,
        message: 'Observação para revenda atualizada com sucesso!',
        data: response.data,
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.message || 'Erro ao atualizar observação para revenda',
        error,
      }
    }
  }

  // Add method to notify "estou na porta"
  const notificarEstouNaPorta = async (pedidoId) => {
    if (!pedidoId) {
      return {
        success: false,
        message: 'ID do pedido não informado',
      }
    }

    try {
      const response = await useApi(PEDIDO_ROTAS.NOTIFICAR_ESTOU_NA_PORTA(pedidoId)).fetch()

      return {
        success: response.status === 'success',
        message: 'Notificação "Estou na porta" enviada com sucesso!',
      }
    } catch (error) {
      return {
        success: false,
        message: 'Erro ao enviar notificação "Estou na porta".',
      }
    }
  }

  // Add methods for client blocking/unblocking
  const bloquearCliente = async (clienteId) => {
    if (!clienteId) {
      return {
        success: false,
        message: 'ID do cliente não informado',
      }
    }

    try {
      const response = await useApi(CLIENTE_ROTAS.BLOQUEAR(clienteId)).post()

      return {
        success: response.status === 'success',
        message: 'Cliente bloqueado com sucesso!',
      }
    } catch (error) {
      return {
        success: false,
        message: 'Erro ao bloquear cliente.',
      }
    }
  }

  const desbloquearCliente = async (clienteId) => {
    if (!clienteId) {
      return {
        success: false,
        message: 'ID do cliente não informado',
      }
    }

    try {
      const response = await useApi(CLIENTE_ROTAS.DESBLOQUEAR(clienteId)).del()

      return {
        success: response.status === 'success',
        message: 'Cliente desbloqueado com sucesso!',
      }
    } catch (error) {
      return {
        success: false,
        message: 'Erro ao desbloquear cliente.',
      }
    }
  }

  // Add method to mark/unmark pedido as pendente
  const atualizarPedidoPendente = async (pedidoId) => {
    if (!pedidoId) {
      return {
        success: false,
        message: 'ID do pedido não informado',
      }
    }

    try {
      const response = await useApi(PEDIDO_ROTAS.PEDIDO_PENDENTE(pedidoId)).put()

      return {
        success: response.status === 'success',
        message: 'Status de pendência do pedido atualizado com sucesso!',
      }
    } catch (error) {
      return {
        success: false,
        message: 'Erro ao atualizar status pendente do pedido.',
      }
    }
  }

  // Add methods for revendedor activation/deactivation
  const ativarRevendedor = async (revendedorId) => {
    if (!revendedorId) {
      return {
        success: false,
        message: 'ID do revendedor não informado',
      }
    }

    try {
      const response = await useApi(REVENDEDOR_ROTAS.ATIVAR_REVENDEDOR(revendedorId)).put()

      // Fix: Ensure we always return the correct format with a message
      return {
        success: response.status === 'success',
        message: response.message || 'Revendedor ativado com sucesso!',
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.message || 'Erro ao ativar revendedor.',
      }
    }
  }

  const desativarRevendedor = async (revendedorId, tempoRecarga) => {
    if (!revendedorId) {
      return {
        success: false,
        message: 'ID do revendedor não informado',
      }
    }

    try {
      const response = await useApi(REVENDEDOR_ROTAS.DESATIVAR_REVENDEDOR(revendedorId)).put({
        tempo_recarga: tempoRecarga,
      })

      return {
        success: response.status === 'success',
        message: 'Revendedor desativado com sucesso!',
      }
    } catch (error) {
      return {
        success: false,
        message: 'Erro ao desativar revendedor.',
      }
    }
  }

  // Add method to apply pedido solutions
  const aplicarSolucaoPedido = async (pedidoId, idacao) => {
    if (!pedidoId) {
      return {
        success: false,
        message: 'ID do pedido não informado',
      }
    }

    try {
      const response = await useApi(PEDIDO_ROTAS.SOLUCOES(pedidoId)).post({
        idacao,
      })

      return {
        success: response.status === 'success',
        message: response.message || 'Ação aplicada com sucesso!',
        data: response.data,
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.message || 'Erro ao aplicar solução ao pedido.',
      }
    }
  }

  // Add method for accepting a pedido specifically
  const aceitarPedido = async (pedidoId, idrevenda) => {
    if (!pedidoId) {
      return {
        success: false,
        message: 'ID do pedido não informado',
      }
    }

    try {
      const response = await useApi(PEDIDO_ROTAS.ACEITAR_PEDIDO(pedidoId)).post({
        idrevenda,
      })

      // If successful and we have escopo_atual info, move the pedido between columns
      if (response.status === 'success' && response.data && response.data.escopo_atual) {
        const sourceColumnKey = findPedidoColumn(pedidoId)
        const targetColumnKey = escopoToColumnKey[response.data.escopo_atual]

        if (sourceColumnKey && targetColumnKey && sourceColumnKey !== targetColumnKey) {
          // Move between columns with manual counting adjustments
          removerPedidoDaColuna(sourceColumnKey, pedidoId, true)
          adicionarPedidoNaColuna(targetColumnKey, response.data, true)
        }
      }

      return {
        success: response.status === 'success',
        message: response.message || 'Pedido aceito com sucesso!',
        data: response.data || null,
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.message || 'Erro ao aceitar pedido.',
      }
    }
  }

  // Add method to request assistance for a pedido
  const solicitarAssistencia = async (pedidoId, data) => {
    if (!pedidoId) {
      return {
        success: false,
        message: 'ID do pedido não informado',
      }
    }

    try {
      const response = await useApi(PEDIDO_ROTAS.SOLICITAR_ASSISTENCIA(pedidoId)).post(data)

      return {
        success: response.status === 'success',
        message: response.message || 'Assistência solicitada com sucesso!',
        data: response.data,
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.message || 'Erro ao solicitar assistência.',
        error,
      }
    }
  }

  // Add method to remove suspicious flag from a pedido
  const removerSuspeitoPedido = async (pedidoId) => {
    if (!pedidoId) {
      return {
        success: false,
        message: 'ID do pedido não informado',
      }
    }
    try {
      const response = await useApi(PEDIDO_ROTAS.REMOVER_SUSPEITO(pedidoId)).put()

      const sourceColumnKey = 'suspeitos'
      const targetColumnKey = escopoToColumnKey[response.data.escopo_atual]

      if (sourceColumnKey && targetColumnKey && sourceColumnKey !== targetColumnKey) {
        // Move between columns with manual counting adjustments
        removerPedidoDaColuna(sourceColumnKey, pedidoId, true)
        adicionarPedidoNaColuna(targetColumnKey, response.data, true)
      }

      return {
        success: response.status === 'success',
        message: response.message || 'Pedido marcado como não suspeito!',
        data: response.data,
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.message || 'Erro ao remover suspeito do pedido.',
      }
    }
  }

  // Método para fechar o snackbar
  const closeErrorSnackbar = () => {
    errorSnackbar.show = false
  }

  // Simplified version that only manages the column data array, not the totals
  const removerPedidoDaColuna = (columnKey, pedidoId, updateCount = false) => {
    if (!state.columnData[columnKey]) return

    const index = state.columnData[columnKey].findIndex((p) => p.id === pedidoId)
    if (index !== -1) {
      // Use splice to maintain reactivity
      state.columnData[columnKey].splice(index, 1)

      // Only update counter if explicitly requested (manual actions, not auto-fetches)
      if (updateCount && state.columnMeta[columnKey] && state.columnMeta[columnKey].total > 0) {
        state.columnMeta[columnKey].total -= 1
      }
    }
  }

  // Get target column from escopo_atual
  const getColunaPorEscopo = (escopoAtual) => {
    return escopoToColumnKey[escopoAtual] || null
  }

  const adicionarPedidoNaColuna = (columnKey, pedido, updateCount = false) => {
    if (!state.columnData[columnKey]) {
      state.columnData[columnKey] = []
    }

    // For "Merece Atenção" column, don't remove from other columns
    if (columnKey === 'mereceatencao') {
      // Just add or update in this column without changing other columns
      const existingIndex = state.columnData[columnKey].findIndex((p) => p.id === pedido.id)

      if (existingIndex !== -1) {
        // Update existing pedido without changing count
        state.columnData[columnKey][existingIndex] = pedido
      } else {
        // Add to the beginning for visibility
        state.columnData[columnKey].unshift(pedido)

        // Setup tab for the pedido if not already set
        if (!state.activeTab[pedido.id]) {
          state.activeTab[pedido.id] = 'tabPedido'
        }

        // Only increment counter if explicitly requested
        if (updateCount && state.columnMeta[columnKey]) {
          state.columnMeta[columnKey].total += 1
        }
      }
      return
    }

    // Regular column handling (unchanged)
    // Check if pedido already exists in this column
    const existingIndex = state.columnData[columnKey].findIndex((p) => p.id === pedido.id)

    if (existingIndex !== -1) {
      // Update existing pedido without changing count
      state.columnData[columnKey][existingIndex] = pedido
    } else {
      // For 'atrasados' column, add to the end of the list
      // For other columns, add to the beginning for visibility
      if (columnKey === 'atrasados' || columnKey === 'aceitar') {
        state.columnData[columnKey].push(pedido) // Add to the end
      } else {
        state.columnData[columnKey].unshift(pedido) // Add to the beginning
      }

      // Setup tab for the pedido if not already set
      if (!state.activeTab[pedido.id]) {
        state.activeTab[pedido.id] = 'tabPedido'
      }

      // Only increment counter if explicitly requested (manual actions, not auto-fetches)
      if (updateCount && state.columnMeta[columnKey]) {
        state.columnMeta[columnKey].total += 1
      }
    }
  }

  const rejeitarPedido = async (pedidoId, idrevenda, motivo) => {
    if (!pedidoId) {
      return {
        success: false,
        message: 'ID do pedido não informado',
      }
    }

    try {
      const response = await useApi(PEDIDO_ROTAS.REJEITAR_PEDIDO(pedidoId)).post({
        idrevenda,
        motivo,
      })

      // If successful and we have escopo_atual info, move the pedido between columns
      if (response.status === 'success' && response.data && response.data.escopo_atual) {
        const sourceColumnKey = findPedidoColumn(pedidoId)
        const targetColumnKey = escopoToColumnKey[response.data.escopo_atual]

        if (sourceColumnKey && targetColumnKey && sourceColumnKey !== targetColumnKey) {
          removerPedidoDaColuna(sourceColumnKey, pedidoId, true)
          adicionarPedidoNaColuna(targetColumnKey, response.data, true)
        }
      }

      return {
        success: response.status === 'success',
        message: response.message || 'Pedido rejeitado com sucesso!',
        data: response.data || null,
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.message || 'Erro ao rejeitar pedido.',
      }
    }
  }

  const refazerPedido = async (pedidoId, idrevenda, motivo) => {
    if (!pedidoId) {
      return {
        success: false,
        message: 'ID do pedido não informado',
      }
    }

    try {
      const response = await useApi(PEDIDO_ROTAS.REFAZER_PEDIDO(pedidoId)).post({
        idrevenda,
        motivo,
      })

      // If successful and we have escopo_atual info, move the pedido between columns
      if (
        response.status === 'success' &&
        response.data &&
        response.data.pedido_rejeitado &&
        response.data.pedido_rejeitado.escopo_atual
      ) {
        const sourceColumnKey = findPedidoColumn(pedidoId)
        const targetColumnKey = escopoToColumnKey[response.data.pedido_rejeitado.escopo_atual]

        if (sourceColumnKey && targetColumnKey && sourceColumnKey !== targetColumnKey) {
          // Move between columns with manual counting adjustments
          removerPedidoDaColuna(sourceColumnKey, pedidoId, true)
          adicionarPedidoNaColuna(targetColumnKey, response.data.pedido_rejeitado, true)
        }
      }

      return {
        success: response.status === 'success',
        message: response.message || 'Pedido rejeitado com sucesso!',
        data: response.data || null,
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.message || 'Erro ao refazer pedido.',
      }
    }
  }

  // Helper to find which column contains a specific pedido
  const findPedidoColumn = (pedidoId) => {
    // Don't consider the mereceatencao column as a source for moving pedidos
    for (const column of statusColumns.value) {
      const columnKey = column.key
      if (
        columnKey !== 'mereceatencao' &&
        columnKey !== 'atencao' &&
        state.columnData[columnKey]?.some((p) => p.id === pedidoId)
      ) {
        return columnKey
      }
    }
    return null
  }

  // Add method for marking a pedido as delivered
  const pedidoEntregue = async (pedidoId, idrevenda) => {
    if (!pedidoId) {
      return {
        success: false,
        message: 'ID do pedido não informado',
      }
    }

    try {
      const response = await useApi(PEDIDO_ROTAS.PEDIDO_ENTREGUE(pedidoId)).post({
        idrevenda,
      })

      // If successful and we have escopo_atual info, move the pedido between columns
      if (response.status === 'success' && response.data && response.data.escopo_atual) {
        const sourceColumnKey = findPedidoColumn(pedidoId)
        const targetColumnKey = escopoToColumnKey[response.data.escopo_atual]

        if (sourceColumnKey && targetColumnKey && sourceColumnKey !== targetColumnKey) {
          // Move between columns with manual counting adjustments
          removerPedidoDaColuna(sourceColumnKey, pedidoId, true)
          adicionarPedidoNaColuna(targetColumnKey, response.data, true)
        }
      }

      return {
        success: response.status === 'success',
        message: response.message || 'Pedido marcado como entregue com sucesso!',
        data: response.data || null,
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.message || 'Erro ao marcar pedido como entregue.',
      }
    }
  }

  const loading = ref(false)
  const pedidoRealizado = ref(null)

  // Realizar um pedido
  const realizarPedido = async (pedidoData) => {
    loading.value = true
    state.error = null
    pedidoRealizado.value = null

    try {
      const response = await useApi(PEDIDO_ROTAS.REALIZAR_PEDIDO).post(pedidoData)

      // Armazenar a resposta completa do servidor
      if (response?.status === 'success') {
        pedidoRealizado.value = response
        return response
      } else {
        throw new Error(response?.message || 'Erro ao realizar pedido')
      }
    } catch (err) {
      // Preservar o erro original incluindo o objeto response
      if (err.response) {
        // Propagar o erro com a resposta intacta
        throw {
          message: err.response.data?.message || 'Erro ao realizar pedido',
          response: err.response,
          status: err.response.status,
        }
      } else {
        // Erro genérico sem resposta do servidor
        state.error = err.message || 'Ocorreu um erro ao realizar o pedido'
        throw err
      }
    } finally {
      loading.value = false
    }
  }

  // Limpar os dados do pedido realizado
  const limparPedidoRealizado = () => {
    pedidoRealizado.value = null
    state.error = null
  }

  const notificarAtraso = async (pedidoId) => {
    if (!pedidoId) {
      return {
        success: false,
        message: 'ID do pedido não informado',
      }
    }

    try {
      const response = await useApi(PEDIDO_ROTAS.NOTIFICAR_ATRASO(pedidoId)).fetch()
      return {
        success: response.status === 'success',
        message: response.message || 'Notificação de atraso enviada com sucesso!',
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao enviar notificação de atraso.',
      }
    }
  }

  const notificarAtrasoCliente = async (pedidoId) => {
    if (!pedidoId) {
      return {
        success: false,
        message: 'ID do pedido não informado',
      }
    }

    try {
      const response = await useApi(PEDIDO_ROTAS.NOTIFICAR_ATRASO_CLIENTE(pedidoId)).fetch()
      return {
        success: response.status === 'success',
        message: response.message || 'Notificação de atraso enviada ao cliente com sucesso!',
      }
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message || 'Erro ao enviar notificação de atraso ao cliente.',
      }
    }
  }

  const solicitarLigacaoAtraso = async (pedidoId) => {
    if (!pedidoId) {
      return {
        success: false,
        message: 'ID do pedido não informado',
      }
    }

    try {
      const response = await useApi(PEDIDO_ROTAS.LIGACAO_ATRASO(pedidoId)).fetch()

      return {
        success: response.status === 'success',
        message: response.message || 'Ligação agendada com sucesso!',
        data: response.data || null,
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao agendar ligação de atraso.',
        error,
      }
    }
  }

  // Add method for marking a pedido as "saiu para entrega" (left for delivery)
  const sairParaEntrega = async (pedidoId, idrevenda, minutosEntrega) => {
    if (!pedidoId) {
      return {
        success: false,
        message: 'ID do pedido não informado',
      }
    }

    try {
      const response = await useApi(PEDIDO_ROTAS.PEDIDO_SAIU_PARA_ENTREGA(pedidoId)).post({
        idrevenda,
        minutos: minutosEntrega,
      })

      // caso o pedido tenha sido aceito e o escopo atual tenha mudado, mover entre colunas
      if (response.status === 'success' && response.data && response.data.escopo_atual) {
        const sourceColumnKey = findPedidoColumn(pedidoId)
        const targetColumnKey = escopoToColumnKey[response.data.escopo_atual]

        if (sourceColumnKey && targetColumnKey && sourceColumnKey !== targetColumnKey) {
          // mover entre colunas com ajustes manuais de contagem
          removerPedidoDaColuna(sourceColumnKey, pedidoId, true)
          adicionarPedidoNaColuna(targetColumnKey, response.data, true)
        }
      }

      return {
        success: response.status === 'success',
        message: response.message || 'Pedido marcado como saiu para entrega com sucesso!',
        data: response.data || null,
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.message || 'Erro ao marcar pedido como saiu para entrega.',
      }
    }
  }

  // Add the new method to remove "Merece Atenção" flag from a client
  const removerMereceAtencaoCliente = async (clienteId) => {
    if (!clienteId) {
      return {
        success: false,
        message: 'ID do cliente não informado',
      }
    }

    try {
      const response = await useApi(CLIENTE_ROTAS.REMOVER_MERECE_ATENCAO(clienteId)).del()

      return {
        success: response.status === 'success',
        message: response.message || 'Cliente não merece mais atenção especial',
        data: response.data,
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.message || 'Erro ao remover indicação de atenção especial.',
        error,
      }
    }
  }

  // Add state for pedidos in attention
  const pedidosEmAtencao = ref([])
  const pedidosEmAtencaoMap = ref(new Map())

  // Load pedidos from localStorage on initialization
  const initializePedidosEmAtencao = () => {
    try {
      const savedPedidos = localStorage.getItem('pedidosEmAtencao')
      if (savedPedidos) {
        const pedidosIds = JSON.parse(savedPedidos)
        pedidosEmAtencao.value = pedidosIds
      }
    } catch (error) { }
  }

  // Save pedidos em atenção to localStorage
  const savePedidosEmAtencao = () => {
    try {
      localStorage.setItem('pedidosEmAtencao', JSON.stringify(pedidosEmAtencao.value))
      state.columnMeta['atencao'] = {
        currentPage: 1,
        total: pedidosEmAtencao.value.length,
        perPage: 50,
        lastPage: 1,
      }
    } catch (error) { }
  }

  // Add pedido to attention list
  const addPedidoEmAtencao = async (pedido) => {
    if (!pedidosEmAtencao.value.includes(pedido.id)) {
      pedidosEmAtencao.value.push(pedido.id)

      try {
        // Fetch fresh data from the backend to ensure we have the latest
        const freshData = await fetchPedidoById(pedido.id)
        if (freshData) {
          pedidosEmAtencaoMap.value.set(pedido.id, freshData)
        } else {
          // If fetching fails, use the provided pedido
          pedidosEmAtencaoMap.value.set(pedido.id, pedido)
        }
      } catch (error) {
        // Fall back to using the provided pedido object
        pedidosEmAtencaoMap.value.set(pedido.id, pedido)
      }

      savePedidosEmAtencao()
    }
  }

  // Remove pedido from attention list
  const removePedidoEmAtencao = (pedidoId) => {
    pedidosEmAtencao.value = pedidosEmAtencao.value.filter((id) => id !== pedidoId)
    pedidosEmAtencaoMap.value.delete(pedidoId)
    savePedidosEmAtencao()
  }

  // Check if pedido is in attention list
  const isPedidoEmAtencao = (pedidoId) => {
    return pedidosEmAtencao.value.includes(pedidoId)
  }

  // Get pedidos in attention as an array - required for the attention column
  const getPedidosEmAtencao = () => {
    return Array.from(pedidosEmAtencaoMap.value.values())
  }

  // Update pedidos in attention from status columns
  const updatePedidosEmAtencaoFromStatusColumns = () => {
    // Make sure pedidosEmAtencao is up-to-date with what's in the map
    pedidosEmAtencao.value.forEach((pedidoId) => {
      if (!pedidosEmAtencaoMap.value.has(pedidoId)) {
        // If pedido is in the attention list but not in the map, try to find it in the columns
        statusColumns.value.forEach((column) => {
          if (column.key !== 'atencao') {
            const pedido = state.columnData[column.key]?.find((p) => p.id === pedidoId)
            if (pedido) {
              pedidosEmAtencaoMap.value.set(pedidoId, pedido)
            }
          }
        })
      }
    })

    // Update any pedidos that might have changed
    for (const [pedidoId, pedido] of pedidosEmAtencaoMap.value.entries()) {
      statusColumns.value.forEach((column) => {
        if (column.key !== 'atencao') {
          const updatedPedido = state.columnData[column.key]?.find((p) => p.id === pedidoId)
          if (updatedPedido) {
            pedidosEmAtencaoMap.value.set(pedidoId, updatedPedido)
          }
        }
      })
    }
  }

  // Fetch a specific pedido by ID
  const fetchPedidoById = async (pedidoId) => {
    try {
      const response = await useApi(PEDIDO_ROTAS.PEDIDO(pedidoId)).fetch()
      return response.data
    } catch (error) {
      return null
    }
  }

  // Add a method to refresh all pedidos in the attention column - optimized version
  const atualizarPedidosEmAtencao = async () => {
    if (pedidosEmAtencao.value.length === 0) return

    try {
      // Concatenate all IDs to make a single API call
      const pedidoIds = pedidosEmAtencao.value.join(',')

      // Make a single API call with the ids parameter
      const response = await useApi(PEDIDO_ROTAS.LISTAR_PEDIDOS).fetch({
        params: { ids: pedidoIds },
      })

      if (response.data && Array.isArray(response.data)) {
        // Update map with fresh data from the batch response
        response.data.forEach((pedido) => {
          if (pedido && pedido.id) {
            pedidosEmAtencaoMap.value.set(pedido.id, pedido)
          }
        })
      }
    } catch (error) { }
  }

  return {
    // State
    get data() {
      return state.data
    },
    get loading() {
      return state.loading
    },
    get error() {
      return state.error
    },
    get activeTab() {
      return state.activeTab
    },
    statusColumns,
    filtros,
    columnMenuOpen,
    get pagination() {
      return state.pagination
    },
    get lastUpdated() {
      return state.lastUpdated
    },

    // Computed
    pedidos,
    visibleColumns,
    hasVisibleColumns,
    managersColumns,

    getPedidosByStatus,

    // Methods for infinite scrolling
    buscarMaisPedidosColuna,
    existemPedidosParaCarregar,
    isColumnLoading,
    getColumnTotal,

    // Methods - API
    fetchPedidos,
    buscarPedidosPorColuna,

    // Methods - Filters
    aplicarFiltros,
    limparFiltros,

    // Methods - Column visibility
    toggleAllColumns,
    saveColumnVisibility,
    loadColumnVisibility,

    // Methods - Column order
    salvarOrdemDasColunas,
    carregarOrdemDasColunas,

    // Methods - Utilities
    getPedidosLength,
    hasPedidos,

    // Methods - Setup
    initialize,
    setActiveTab,

    editarPedido,
    avisarPedido,
    atualizarObsBoasVindas,
    atualizarObservacaoPedido,
    atualizarObservacaoAplicativo,
    notificarEstouNaPorta,
    bloquearCliente,
    desbloquearCliente,
    atualizarPedidoPendente,
    ativarRevendedor,
    desativarRevendedor,
    aplicarSolucaoPedido,
    aceitarPedido,
    pedidoEntregue,
    solicitarAssistencia,
    removerSuspeitoPedido,

    // Expose error snackbar
    errorSnackbar,
    closeErrorSnackbar,

    removerPedidoDaColuna,

    // Add the new methods to the returned object
    getColunaPorEscopo,
    adicionarPedidoNaColuna,
    rejeitarPedido,
    findPedidoColumn,
    refazerPedido,

    realizarPedido,
    limparPedidoRealizado,
    pedidoRealizado,
    notificarAtraso,
    notificarAtrasoCliente,
    sairParaEntrega,
    removerMereceAtencaoCliente,

    // Export new attention-related functions
    pedidosEmAtencao,
    addPedidoEmAtencao,
    removePedidoEmAtencao,
    isPedidoEmAtencao,
    getPedidosEmAtencao,
    updatePedidosEmAtencaoFromStatusColumns,
    atualizarPedidosEmAtencao,
    fetchPedidoById,
    solicitarLigacaoAtraso,
    getListManagers,
  }
})
